/* eslint-disable max-lines */
import { Text, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
// eslint-disable-next-line no-restricted-imports -- TextField is needed for multiline input
import { Alert, Box, Stack, TextField, ThemeProvider } from "@mui/material";
import { invalidateClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/invalidateClipboardScoreProfile";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { isSameOptionalMinute } from "@src/appV2/lib/utils/isSameOptionalMinute";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { getEffectiveGeofenceArrival } from "@src/appV2/redesign/WorkerShifts/MissedPunches/utils/getEffectiveGeofenceArrival";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { ExtraWorkedTimeType } from "@src/appV2/Shifts/Shift/types";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { addHours, addMinutes, isAfter, isBefore, parseISO } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { PageFooterWrapper } from "../../../components/PageFooterWrapper";
import { PageWrapper } from "../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../components/StickyScrollPageHeader";
import { type Duration, minutesToDuration } from "../../../DateTime/DurationInput";
import { PageContent } from "../../components/PageContent";
// TODO: Re-enable when backend endpoint is ready
// import { useValidateTimecard } from "../../MissedPunches/api/useValidateTimecard";
import { validateBreakTimestamps } from "../../MissedPunches/utils/validateBreakTimestamps";
import { validateClockInTimestamp } from "../../MissedPunches/utils/validateClockInTimestamp";
import { validateClockOutTimestamp } from "../../MissedPunches/utils/validateClockOutTimestamp";
import { MY_SHIFTS_FULL_PATH } from "../../path";
import { useIsTimekeepingIntegrationsEnabled } from "../../Timekeeping/TimekeepingIntegrations/useIsTimekeepingIntegrationsEnabled";
import { FacilitySignatureSection } from "../../Timekeeping/Timesheet/components/FacilitySignatureSection";
import { ReadOnlyTimesheetTimeEntries } from "../../Timekeeping/Timesheet/components/ReadOnlyTimesheetTimeEntries";
import { TimesheetTimeEntries } from "../../Timekeeping/Timesheet/components/TimesheetTimeEntries";
import { WorkerSignatureSection } from "../../Timekeeping/Timesheet/components/WorkerSignatureSection";
import { EditBreakTimeBottomSheet } from "../../Timekeeping/Timesheet/EditBreakTimeBottomSheet";
import { EditClockInTimeBottomSheet } from "../../Timekeeping/Timesheet/EditClockInTimeBottomSheet";
import { EditClockOutTimeBottomSheet } from "../../Timekeeping/Timesheet/EditClockOutTimeBottomSheet";
import { useTimesheetEntry } from "../../Timekeeping/Timesheet/hooks/useTimesheetEntry";
import {
  type SubmitTimesheetFormValues,
  useTimesheetSubmission,
} from "../../Timekeeping/Timesheet/hooks/useTimesheetSubmission";
import { useTimesheetSubmissionTimer } from "../../Timekeeping/Timesheet/hooks/useTimesheetSubmissionTimer";
import { calculateAutoBreakTimes } from "../../Timekeeping/Timesheet/utils/calculateAutoBreakTimes";
import { calculateBreakDuration } from "../../Timekeeping/Timesheet/utils/calculateBreakDuration";
import { formatBreakDisplay } from "../../Timekeeping/Timesheet/utils/formatBreakDisplay";
import { isMandatoryBreakRequired } from "../../Timekeeping/Timesheet/utils/isMandatoryBreakRequired";
import { TimesheetSubmittedDialog } from "../../Timekeeping/TimesheetSubmittedDialog/TimesheetSubmittedDialog";
import { useGetShiftDetailsData } from "../api/useGetShiftDetailsData";
import {
  SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY,
  UNVERIFIED_TIMESHEET_RETURN_PATH_STATE_KEY,
} from "../constants";
import { SubmitUnverifiedTimesheetConfirmationBottomSheet } from "./Dialogs/SubmitUnverifiedTimesheetConfirmationBottomSheet";
import { SubmitTimesheetPageSkeleton } from "./SubmitTimesheetPageSkeleton";

interface RouteParams {
  shiftId: string;
}

const ERROR_MESSAGES = {
  INTERNAL_TIMECLOCK_DISABLED:
    "Timestamps provided from an internal time clock cannot be modified.",
  TIMESHEET_EDITING_DISABLED: "Timesheet editing is currently disabled for this facility.",
} as const;

// Non-IP timesheet uses facility-signing and worker-signing steps
// (instead of facility-signing and hcp-signing used by IP timesheets)
type UnverifiedTimesheetStep = "facility-signing" | "worker-signing";

export function SubmitUnverifiedTimesheetPage() {
  const { shiftId } = useParams<RouteParams>();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showErrorToast } = useToast();
  const worker = useDefinedWorker();
  const workerId = worker.userId;
  const workerName = `${worker.firstName ?? ""} ${worker.lastName ?? ""}`.trim();
  // Check for skipWorkplaceSignature query param to skip signature flow
  const isSkipWorkplaceSignatureFlow = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get(SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY) === "true";
  }, [location.search]);

  const returnPath = useMemo(() => {
    const state = location.state as Record<string, unknown> | undefined;
    const path = state?.[UNVERIFIED_TIMESHEET_RETURN_PATH_STATE_KEY];
    return typeof path === "string" ? path : MY_SHIFTS_FULL_PATH;
  }, [location.state]);

  useLogEffect(
    APP_V2_USER_EVENTS.TIMESHEET_SUBMISSION_PAGE_VIEWED,
    { shiftId, isNonIP: true },
    { enabled: isDefined(shiftId) }
  );

  const { stopTimesheetSubmissionTimer } = useTimesheetSubmissionTimer(shiftId);

  const scrollRef = useRef<HTMLDivElement>(null);
  const editClockInModalState = useModalState();
  const editClockOutModalState = useModalState();
  const editBreakModalState = useModalState();
  const timesheetSubmittedModalState = useModalState();
  const submitUnverifiedTimesheetConfirmationModalState = useModalState();

  const {
    shift,
    shiftState,
    shiftError,
    isLoading: isShiftDataLoading,
  } = useGetShiftDetailsData({
    shiftId: shiftId ?? "",
  });

  const timesheetFlow = shift?.verified ? "shift_adjustment" : "non_instant_shift_verification";
  const formTimesheet = useTimesheetEntry(shift);
  const workplaceTimezone = shift?.facility?.tmz ?? DEFAULT_TIMEZONE;
  const isCaliforniaFacility = shift?.facility?.featureSettings?.californiaTimesheet ?? false;
  const isEnforceGeofenceTimestampsEnabled = Boolean(
    shiftState?.metadata.isEnforceGeofenceTimestampsEnabled
  );
  const isGeofenceFacility = isDefined(shift?.geofence?.entry);
  const isNfcOrMeshFacility = Boolean(shift?.nfcTag);

  const arrivalTime = shift?.geofence?.entry ? parseISO(shift.geofence.entry) : undefined;
  const departureTime = shift?.geofence?.exit ? parseISO(shift.geofence.exit) : undefined;

  const earliestPlausibleArrivalTime = shift?.attendance?.earliestPlausibleArrivalAt
    ? parseISO(shift.attendance.earliestPlausibleArrivalAt)
    : undefined;
  const latestPlausibleDepartureTime = shift?.attendance?.latestPlausibleDepartureAt
    ? parseISO(shift.attendance.latestPlausibleDepartureAt)
    : undefined;

  // Step management: California facilities require facility signature first, then worker signature
  const [currentStep, setCurrentStep] = useState<UnverifiedTimesheetStep>("facility-signing");

  const workplaceId = shift?.facility?.userId;

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId,
  });

  const timekeepingIntegrationsConfig = useIsTimekeepingIntegrationsEnabled({
    config: shiftState?.metadata.externalTimekeepingIntegrationsConfig,
  });
  const isTimekeepingIntegrationsEnabled = isDefined(timekeepingIntegrationsConfig);
  const isTimesheetEditDisabled = shiftState?.metadata.isTimeSheetEditDisabled ?? false;

  // TODO: Re-enable when backend endpoint is ready
  // const { mutateAsync: validateTimecardAPI, isLoading: isValidating } = useValidateTimecard();

  // Map unverified timesheet steps to standard timesheet steps for useTimesheetSubmission
  function getTimesheetSubmissionStep(): "facility-signing" | "hcp-signing" | undefined {
    if (isSkipWorkplaceSignatureFlow && isCaliforniaFacility) {
      return "hcp-signing";
    }

    if (currentStep === "facility-signing") {
      return "facility-signing";
    }

    if (isCaliforniaFacility && currentStep === "worker-signing") {
      return "hcp-signing";
    }

    return undefined;
  }

  const {
    handleSubmit: handleTimesheetSubmit,
    isLoading: isSubmissionLoading,
    formSchema,
  } = useTimesheetSubmission({
    shift,
    isCaliforniaFacility,
    currentStep: getTimesheetSubmissionStep(),
    isUnverifiedTimesheetFlow: true,
    isSkipWorkplaceSignatureFlow,
    actualClockInOut:
      shift?.clockInOut?.start && shift?.clockInOut?.end
        ? { start: shift.clockInOut.start, end: shift.clockInOut.end }
        : undefined,
    onSuccess: async () => {
      const duration = stopTimesheetSubmissionTimer();
      logEvent(APP_V2_USER_EVENTS.TIMESHEET_SUBMITTED, {
        shiftId,
        workerId,
        workplaceId,
        isCaliforniaFacility,
        step: currentStep,
        isNonIP: true,
        timesheetFlow,
        isSkipWorkplaceSignatureFlow,
        durationMs: duration?.totalDurationMs,
        activeDurationMs: duration?.activeDurationMs,
      });
      invalidateClipboardScoreProfile(queryClient, workerId);
      submitUnverifiedTimesheetConfirmationModalState.openModal();
    },
    onError: (errorMessage) => {
      logEvent(APP_V2_USER_EVENTS.TIMESHEET_SUBMISSION_FAILED, {
        shiftId,
        workerId,
        workplaceId,
        isCaliforniaFacility,
        step: currentStep,
        errorMessage,
        isNonIP: true,
        timesheetFlow,
        isSkipWorkplaceSignatureFlow,
      });
      showErrorToast(errorMessage);
    },
  });

  const { control, handleSubmit, reset, setValue, watch, formState } =
    useForm<SubmitTimesheetFormValues>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        clockInTime: new Date(),
        clockOutTime: new Date(),
        breakStartTime: undefined,
        breakEndTime: undefined,
        missedPunches: [{ type: ExtraWorkedTimeType.FULL_SHIFT, reason: "" }],
        breakAuthorizationReason: undefined,
        // California facility fields
        facilityEmployeeName: "",
        facilityEmployeeSignature: "",
        workerStation: "",
        workerNursingAssignment: "",
        hcpName: "",
        hcpSignature: "",
      },
      mode: "onChange",
    });

  const { isValid: isFormStateValid } = formState;

  const formClockInTime = watch("clockInTime");
  const formClockOutTime = watch("clockOutTime");
  const formBreakStartTime = watch("breakStartTime");
  const formBreakEndTime = watch("breakEndTime");

  const isMandatoryBreakRequiredForCurrentFormValues = useMemo(() => {
    if (!isDefined(shift) || !isDefined(shiftState) || !formClockInTime || !formClockOutTime) {
      return false;
    }

    return isMandatoryBreakRequired({
      requiresLunchBreak: shift.facility.requiresLunchBreak ?? false,
      solveUnpaidBreaksRolloutStageWithTargetingConfig:
        shiftState.metadata.solveUnpaidBreaksRolloutStageWithTargetingConfig,
      clockInTime: formClockInTime,
      clockOutTime: formClockOutTime,
    });
  }, [formClockInTime, formClockOutTime, shift, shiftState]);

  // Initialize form once shift data is available
  const formInitialized = useRef(false);
  useEffect(() => {
    if (formInitialized.current || !isDefined(shift) || !isDefined(shiftState)) {
      return;
    }

    const scheduledClockInTime = formTimesheet?.clockInTime ?? parseISO(shift.start);
    const scheduledClockOutTime = formTimesheet?.clockOutTime ?? parseISO(shift.end);

    const { value: arrivalTimestamp } = getEffectiveGeofenceArrival(
      arrivalTime,
      earliestPlausibleArrivalTime,
      isEnforceGeofenceTimestampsEnabled
    );

    const clockInTime =
      !shift.verified &&
      isDefined(arrivalTimestamp) &&
      isAfter(arrivalTimestamp, scheduledClockInTime)
        ? arrivalTimestamp
        : scheduledClockInTime;

    const clockOutTime = (() => {
      if (shift.verified) {
        return scheduledClockOutTime;
      }

      if (
        isEnforceGeofenceTimestampsEnabled &&
        isDefined(latestPlausibleDepartureTime) &&
        isBefore(latestPlausibleDepartureTime, scheduledClockOutTime)
      ) {
        return latestPlausibleDepartureTime;
      }

      if (
        !isEnforceGeofenceTimestampsEnabled &&
        isDefined(departureTime) &&
        isBefore(departureTime, scheduledClockOutTime)
      ) {
        return departureTime;
      }

      const now = new Date();
      if (isAfter(scheduledClockOutTime, now)) {
        return now;
      }

      return scheduledClockOutTime;
    })();

    let breakStartTime = formTimesheet?.breakStartTime;
    let breakEndTime = formTimesheet?.breakEndTime;

    // Auto-add mandatory break if not present and actual form times qualify
    if (!breakStartTime && !breakEndTime && !shift.verified) {
      const isBreakRequiredForActualTimes = isMandatoryBreakRequired({
        requiresLunchBreak: shift.facility.requiresLunchBreak ?? false,
        solveUnpaidBreaksRolloutStageWithTargetingConfig:
          shiftState.metadata.solveUnpaidBreaksRolloutStageWithTargetingConfig,
        clockInTime,
        clockOutTime,
      });

      if (isBreakRequiredForActualTimes) {
        const autoBreak = calculateAutoBreakTimes(
          clockInTime,
          clockOutTime,
          mandatoryBreakDurationInMinutes
        );
        if (isDefined(autoBreak)) {
          breakStartTime = autoBreak.autoBreakStartTime;
          breakEndTime = autoBreak.autoBreakEndTime;
        }
      }
    }

    reset({
      clockInTime,
      clockOutTime,
      breakStartTime,
      breakEndTime,
      missedPunches: [{ type: ExtraWorkedTimeType.FULL_SHIFT, reason: "" }],
      breakAuthorizationReason: undefined,
      // California facility fields
      facilityEmployeeName: "",
      facilityEmployeeSignature: "",
      workerStation: "",
      workerNursingAssignment: "",
      hcpName: `${worker.firstName ?? ""} ${worker.lastName ?? ""}`.trim(),
      hcpSignature: "",
    });

    formInitialized.current = true;
  }, [
    shift,
    shiftState,
    formTimesheet,
    mandatoryBreakDurationInMinutes,
    reset,
    worker.firstName,
    worker.lastName,
    earliestPlausibleArrivalTime,
    arrivalTime,
    latestPlausibleDepartureTime,
    departureTime,
    isEnforceGeofenceTimestampsEnabled,
  ]);

  // displayTimesheet is always defined once we have form values
  const displayTimesheet = useMemo(
    () => ({
      clockInTime: formClockInTime,
      clockOutTime: formClockOutTime,
      breakStartTime: formBreakStartTime,
      breakEndTime: formBreakEndTime,
    }),
    [formClockInTime, formClockOutTime, formBreakStartTime, formBreakEndTime]
  );

  const breakDisplayText = formatBreakDisplay({
    displayTimesheet,
    workplaceTimezone,
  });

  const verifiedClockIn =
    shift?.verified && shift.clockInOut?.start ? parseISO(shift.clockInOut.start) : undefined;

  // Validation logic
  const clockInValidation = useMemo(() => {
    if (!isDefined(shift) || !isDefined(shiftState) || !formClockInTime) {
      return { isValid: true };
    }

    return validateClockInTimestamp({
      selectedTime: formClockInTime,
      geofenceArrivalTime: arrivalTime,
      earliestPlausibleArrivalTime,
      shiftStartAt: parseISO(shift.start),
      facilitySettings: {
        preventEarlyClockIns: !(shiftState.metadata.isEarlyClockInEnabled ?? false),
        earlyClockInMinutes: shiftState.metadata.earlyClockInEnabledInMinutes,
      },
      workerClockIn: shift.clockInOut?.start ? parseISO(shift.clockInOut.start) : undefined,
      clockOutTime: formClockOutTime,
      breakStartTime: formBreakStartTime,
      isGeofenceFacility,
      isNfcOrMeshFacility,
      isEnforceGeofenceTimestampsEnabled,
      verifiedClockIn,
      workplaceTimezone,
    });
  }, [
    shift,
    shiftState,
    formClockInTime,
    arrivalTime,
    earliestPlausibleArrivalTime,
    formClockOutTime,
    formBreakStartTime,
    isGeofenceFacility,
    isNfcOrMeshFacility,
    isEnforceGeofenceTimestampsEnabled,
    verifiedClockIn,
    workplaceTimezone,
  ]);

  const verifiedClockOut =
    shift?.verified && shift.clockInOut?.end ? parseISO(shift.clockInOut.end) : undefined;

  const clockOutValidation = useMemo(() => {
    if (!isDefined(shift) || !formClockInTime || !formClockOutTime) {
      return { isValid: true };
    }

    const isDirectIntegrationFacility = isDefined(
      shiftState?.metadata.externalTimekeepingIntegrationsConfig
    );

    return validateClockOutTimestamp({
      selectedTime: formClockOutTime,
      clockInTime: formClockInTime,
      geofenceDepartureTime: departureTime,
      latestPlausibleDepartureTime,
      shiftEndAt: parseISO(shift.end),
      workerClockOut: shift.clockInOut?.end ? parseISO(shift.clockInOut.end) : undefined,
      isGeofenceFacility,
      isNfcOrMeshFacility,
      isDirectIntegrationFacility,
      isEnforceGeofenceTimestampsEnabled,
      verifiedClockOut,
      workplaceTimezone,
    });
  }, [
    shift,
    shiftState,
    departureTime,
    formClockInTime,
    formClockOutTime,
    verifiedClockOut,
    workplaceTimezone,
    latestPlausibleDepartureTime,
    isGeofenceFacility,
    isNfcOrMeshFacility,
    isEnforceGeofenceTimestampsEnabled,
  ]);

  const verifiedBreakStartTime =
    shift?.verified && shift.lunchInOut?.start ? parseISO(shift.lunchInOut.start) : undefined;
  const verifiedBreakEndTime =
    shift?.verified && shift.lunchInOut?.end ? parseISO(shift.lunchInOut.end) : undefined;
  const breakValidation = useMemo(() => {
    if (!isDefined(shift) || !formClockInTime || !formClockOutTime) {
      return { isValid: true };
    }

    const isBreakSkipped = isNil(formBreakStartTime) && isNil(formBreakEndTime);

    return validateBreakTimestamps({
      breakStartTime: formBreakStartTime,
      breakEndTime: formBreakEndTime,
      clockInTime: formClockInTime,
      clockOutTime: formClockOutTime,
      isShiftVerified: shift?.verified ?? false,
      isBreakSkipped,
      verifiedBreakStartTime,
      verifiedBreakEndTime,
    });
  }, [
    shift,
    formClockInTime,
    formClockOutTime,
    formBreakStartTime,
    formBreakEndTime,
    verifiedBreakEndTime,
    verifiedBreakStartTime,
  ]);

  const isFormModified =
    !isSameOptionalMinute(formClockInTime, verifiedClockIn) ||
    !isSameOptionalMinute(formClockOutTime, verifiedClockOut) ||
    !isSameOptionalMinute(formBreakStartTime, verifiedBreakStartTime) ||
    !isSameOptionalMinute(formBreakEndTime, verifiedBreakEndTime);
  const hasVerifiedNoChangeError = (shift?.verified ?? false) && !isFormModified;
  const hasValidationErrors =
    !clockInValidation.isValid || !clockOutValidation.isValid || !breakValidation.isValid;
  const hasBlockingErrors = hasValidationErrors || hasVerifiedNoChangeError;
  const isFormValid = isFormStateValid && !hasBlockingErrors;

  // Tracks whether the user has ever changed any time fields in this session.
  // We use this to avoid showing the "please adjust at least one of the times above"
  // banner on the initial render of a verified shift (when the user hasn't interacted yet),
  // while still surfacing it if they change times and then revert back to the original values.
  const [hasEverBeenModified, setHasEverBeenModified] = useState(false);
  useEffect(() => {
    if (isFormModified && !hasEverBeenModified) {
      setHasEverBeenModified(true);
    }
  }, [hasEverBeenModified, isFormModified]);

  function getBlockingErrorMessage(): string | undefined {
    if (!clockInValidation.isValid) {
      return clockInValidation.message;
    }

    if (!clockOutValidation.isValid) {
      return clockOutValidation.message;
    }

    if (!breakValidation.isValid) {
      return breakValidation.message;
    }

    if (hasVerifiedNoChangeError && hasEverBeenModified) {
      return "To request a change for this shift, please adjust at least one time field below.";
    }

    return undefined;
  }

  const blockingErrorMessage = getBlockingErrorMessage();

  // Determine if time entries are read-only (during worker signing for California)
  const areTimeEntriesReadOnly = isCaliforniaFacility && currentStep === "worker-signing";

  const isEditingAllowed =
    currentStep === "facility-signing" ||
    (currentStep === "worker-signing" && !isCaliforniaFacility);

  // Check if facility signature should be disabled pending confirmations
  const isFacilitySignaturePadDisabled = isSubmissionLoading;

  function getTimesheetHeaderText() {
    if (isTimekeepingIntegrationsEnabled) {
      return "The following times have been provided by the facility's internal time clock and cannot be changed here.";
    }

    if (isTimesheetEditDisabled) {
      return "Submit your timesheet now to get paid instantly.";
    }

    return "Make sure the times are correct and make any edits before submitting.";
  }

  function showEditingDisabledErrorToast(message: string) {
    showErrorToast(message);
  }

  function applyBreakChange(newBreakStartTime: Date | undefined, newBreakDuration: Duration) {
    setValue("breakStartTime", newBreakStartTime, { shouldValidate: true });

    // Calculate break end time from start time and duration
    if (newBreakDuration.hours === 0 && newBreakDuration.minutes === 0) {
      // Break is skipped
      setValue("breakEndTime", undefined, { shouldValidate: true });
    } else if (newBreakStartTime) {
      // Calculate end time from start time + duration
      let endTime = new Date(newBreakStartTime);
      if (newBreakDuration.hours > 0) {
        endTime = addHours(endTime, newBreakDuration.hours);
      }

      if (newBreakDuration.minutes > 0) {
        endTime = addMinutes(endTime, newBreakDuration.minutes);
      }

      setValue("breakEndTime", endTime, { shouldValidate: true });
    } else {
      // No start time, so no end time
      setValue("breakEndTime", undefined, { shouldValidate: true });
    }
  }

  const onSubmit = useCallback(
    async (data: SubmitTimesheetFormValues) => {
      // For California facilities: handle two-step signature flow (skip if isSkipWorkplaceSignatureFlow)
      if (
        !isSkipWorkplaceSignatureFlow &&
        isCaliforniaFacility &&
        currentStep === "facility-signing"
      ) {
        // Workplace employee has signed, show "pass phone back" dialog
        timesheetSubmittedModalState.openModal();
        return;
      }

      // TODO: Enable backend validation when endpoint is ready
      // Step 1: Validate with backend API
      // await validateTimecardAPI({
      //   shiftId,
      //   data: {
      //     submitClockInOut: {
      //       start: data.clockInTime.toISOString(),
      //       end: data.clockOutTime.toISOString(),
      //     },
      //     submitLunchInOut:
      //       data.breakStartTime && data.breakEndTime
      //         ? {
      //             start: data.breakStartTime.toISOString(),
      //             end: data.breakEndTime.toISOString(),
      //           }
      //         : undefined,
      //   },
      // });

      // Final submission (worker-signing step, non-California, or skip signature flow)
      await handleTimesheetSubmit(data);
    },
    [
      isSkipWorkplaceSignatureFlow,
      isCaliforniaFacility,
      currentStep,
      timesheetSubmittedModalState,
      handleTimesheetSubmit,
    ]
  );

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={{ backgroundColor: (theme) => theme.palette.background.tertiary, paddingX: 0 }}
      >
        <StickyScrollPageHeader
          title={shift?.verified ? "Shift adjustment request" : "Shift payment request"}
          scrollContainerRef={scrollRef}
          headerVariant="tertiary"
          hideBackButton={isCaliforniaFacility && currentStep === "worker-signing"}
        />

        {!isShiftDataLoading && !shiftError && shift ? (
          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column", flex: 1 }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <PageContent ref={scrollRef} sx={{ paddingX: 0 }}>
              <Stack spacing={6} sx={{ paddingX: 6, paddingBottom: 6 }}>
                {/* Blocking Error Banners */}
                {blockingErrorMessage && <Alert severity="error">{blockingErrorMessage}</Alert>}

                {/* Time Entries - Different component based on step */}
                {areTimeEntriesReadOnly ? (
                  <ReadOnlyTimesheetTimeEntries
                    displayTimesheet={displayTimesheet}
                    workplaceTimezone={workplaceTimezone}
                    formatBreakDisplay={() => breakDisplayText}
                  />
                ) : (
                  <>
                    {/* Mandatory Break Info */}
                    {!isTimekeepingIntegrationsEnabled &&
                      !isTimesheetEditDisabled &&
                      isMandatoryBreakRequiredForCurrentFormValues && (
                        <Alert severity="info" sx={{ mb: 4 }}>
                          This facility requires you to take a {mandatoryBreakDurationInMinutes}{" "}
                          minute break if you work more than 5 hours. If you didn&apos;t take a
                          break, please edit the time below.
                        </Alert>
                      )}

                    {/* Header */}
                    <Text variant="body1" sx={{ color: (theme) => theme.palette.text.secondary }}>
                      {getTimesheetHeaderText()}
                    </Text>

                    <TimesheetTimeEntries
                      hidePencilIcons={isTimekeepingIntegrationsEnabled || isTimesheetEditDisabled}
                      disabled={isSubmissionLoading}
                      displayTimesheet={displayTimesheet}
                      workplaceTimezone={workplaceTimezone}
                      formatBreakDisplay={() => breakDisplayText}
                      onEditBreak={() => {
                        logEvent(APP_V2_USER_EVENTS.TIMESHEET_EDIT_BREAK_TAPPED, {
                          shiftId,
                        });

                        if (isTimekeepingIntegrationsEnabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.INTERNAL_TIMECLOCK_DISABLED);
                        } else if (isTimesheetEditDisabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.TIMESHEET_EDITING_DISABLED);
                        } else if (isEditingAllowed) {
                          editBreakModalState.openModal();
                        }
                      }}
                      onEditClockIn={() => {
                        logEvent(APP_V2_USER_EVENTS.TIMESHEET_EDIT_CLOCK_IN_TAPPED, {
                          shiftId,
                        });

                        if (isTimekeepingIntegrationsEnabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.INTERNAL_TIMECLOCK_DISABLED);
                        } else if (isTimesheetEditDisabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.TIMESHEET_EDITING_DISABLED);
                        } else if (isEditingAllowed) {
                          editClockInModalState.openModal();
                        }
                      }}
                      onEditClockOut={() => {
                        logEvent(APP_V2_USER_EVENTS.TIMESHEET_EDIT_CLOCK_OUT_TAPPED, {
                          shiftId,
                        });

                        if (isTimekeepingIntegrationsEnabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.INTERNAL_TIMECLOCK_DISABLED);
                        } else if (isTimesheetEditDisabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.TIMESHEET_EDITING_DISABLED);
                        } else if (isEditingAllowed) {
                          editClockOutModalState.openModal();
                        }
                      }}
                    />
                  </>
                )}

                {/*
                  Single reason field for unverified timesheets (non-IP shifts).
                  For non-IP, there's no hcp_clock_in/out to compare against, so we send FULL_SHIFT type.

                  Future (FOR-1315): Per-timestamp bottom sheets for IP missed punch flow with
                  visual indicators and granular pending amounts per timestamp.
                */}
                <Stack spacing={2}>
                  <Text variant="body2">Write a message to the workplace reviewer*</Text>
                  <Controller
                    name="missedPunches.0.reason"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        multiline
                        required
                        fullWidth
                        disabled={isSubmissionLoading}
                        placeholder={
                          shift?.verified
                            ? "Let the workplace know why you adjusted your shift times"
                            : "Let the workplace know why you missed clocking in or out"
                        }
                        rows={4}
                        error={Boolean(fieldState.error)}
                        helperText={
                          fieldState.error?.message ?? `${field.value?.length ?? 0}/500 characters`
                        }
                        inputProps={{ maxLength: 500 }}
                      />
                    )}
                  />
                </Stack>
              </Stack>

              {/* Facility Signature Section - Only shown when not skipping signature */}
              {!isSkipWorkplaceSignatureFlow && currentStep === "facility-signing" && (
                <FacilitySignatureSection
                  control={control}
                  firstName={worker.firstName ?? ""}
                  isCaliforniaFacility={isCaliforniaFacility}
                  signaturePadIsDisabled={isFacilitySignaturePadDisabled}
                  isValid={isFormValid}
                  submitButtonText={
                    isCaliforniaFacility ? "Submit" : "Request workplace verification"
                  }
                  setValue={setValue}
                  onDisabledSignaturePadTouchStart={() => undefined}
                  onSubmit={handleSubmit(onSubmit)}
                />
              )}

              {/* California Worker Signature Section */}
              {isCaliforniaFacility &&
                (currentStep === "worker-signing" || isSkipWorkplaceSignatureFlow) && (
                  <WorkerSignatureSection
                    control={control}
                    firstName={worker.firstName ?? ""}
                    fullName={workerName}
                    workerStation={watch("workerStation") ?? ""}
                    workerNursingAssignment={watch("workerNursingAssignment") ?? ""}
                    signaturePadIsDisabled={isSubmissionLoading}
                    isSkipWorkplaceSignatureFlow={isSkipWorkplaceSignatureFlow}
                    setValue={setValue}
                  >
                    <LoadingButton
                      fullWidth
                      type="submit"
                      size="large"
                      variant="contained"
                      disabled={!isFormValid}
                      isLoading={isSubmissionLoading}
                      sx={{
                        mt: 6,
                        mb: 6,
                        "&.Mui-disabled": {
                          backgroundColor: (theme) => theme.palette.grey[300],
                          color: (theme) => theme.palette.grey[500],
                        },
                      }}
                      onClick={handleSubmit(onSubmit)}
                    >
                      {isSkipWorkplaceSignatureFlow
                        ? "Submit timesheet without signature"
                        : "Request workplace verification"}
                    </LoadingButton>
                  </WorkerSignatureSection>
                )}
            </PageContent>

            {/* Submit timesheet without signature button */}
            {isSkipWorkplaceSignatureFlow && !isCaliforniaFacility && (
              <PageFooterWrapper>
                <LoadingButton
                  fullWidth
                  type="submit"
                  size="large"
                  variant="contained"
                  disabled={!isFormValid}
                  isLoading={isSubmissionLoading}
                  sx={{
                    "&.Mui-disabled": {
                      backgroundColor: (theme) => theme.palette.grey[300],
                      color: (theme) => theme.palette.grey[500],
                    },
                  }}
                  onClick={handleSubmit(onSubmit)}
                >
                  Submit timesheet without signature
                </LoadingButton>
              </PageFooterWrapper>
            )}
          </Box>
        ) : (
          <PageContent ref={scrollRef} sx={{ paddingX: 0 }}>
            {isShiftDataLoading && <SubmitTimesheetPageSkeleton />}

            {shiftError ? (
              <Alert severity="error" sx={{ mt: 4, mx: 6 }}>
                Something went wrong while loading this shift. Please try again.
              </Alert>
            ) : undefined}
          </PageContent>
        )}

        {/* Edit Modals - Only available when editing is allowed */}
        {isEditingAllowed && isDefined(shift) && isDefined(shiftState) && (
          <>
            <EditClockInTimeBottomSheet
              timesheetFlow={timesheetFlow}
              modalState={editClockInModalState}
              workplaceTimezone={workplaceTimezone}
              shiftStartAt={parseISO(shift.start)}
              shiftEndAt={parseISO(shift.end)}
              isNfcShift={isDefined(shift.nfcTag) && shift.nfcTag}
              earliestGeofenceEntryTime={arrivalTime}
              earliestPlausibleArrivalTime={earliestPlausibleArrivalTime}
              clockInButtonTappedAt={formTimesheet?.clockInTime}
              earlyClockInEnabled={shiftState.metadata.isEarlyClockInEnabled ?? false}
              earlyClockInEnabledInMinutes={shiftState.metadata.earlyClockInEnabledInMinutes}
              clockInTime={formClockInTime}
              clockOutTime={formClockOutTime}
              breakStartTime={formBreakStartTime}
              workerClockIn={shift.clockInOut?.start ? parseISO(shift.clockInOut.start) : undefined}
              isGeofenceFacility={isGeofenceFacility}
              isMissedPunchRequestEnabled={shiftState.metadata.isMissedPunchRequestEnabled}
              isEnforceGeofenceTimestampsEnabled={isEnforceGeofenceTimestampsEnabled}
              verifiedClockIn={
                shift.verified && shift.clockInOut?.start
                  ? parseISO(shift.clockInOut.start)
                  : undefined
              }
              shiftId={shiftId}
              workerId={workerId}
              workplaceId={workplaceId}
              onConfirm={(newClockInTime) => {
                setValue("clockInTime", newClockInTime, { shouldValidate: true });
              }}
            />

            <EditClockOutTimeBottomSheet
              timesheetFlow={timesheetFlow}
              modalState={editClockOutModalState}
              workplaceTimezone={workplaceTimezone}
              shiftStartAt={parseISO(shift.start)}
              shiftEndAt={parseISO(shift.end)}
              clockInTime={formClockInTime}
              clockOutTime={formClockOutTime}
              clockOutButtonTappedAt={formTimesheet?.clockOutTime}
              breakEndTime={formBreakEndTime}
              geofenceDepartureTime={departureTime}
              latestPlausibleDepartureTime={latestPlausibleDepartureTime}
              workerClockOut={shift.clockInOut?.end ? parseISO(shift.clockInOut.end) : undefined}
              isGeofenceFacility={isGeofenceFacility}
              isNfcOrMeshFacility={isNfcOrMeshFacility}
              isDirectIntegrationFacility={isDefined(
                shiftState.metadata.externalTimekeepingIntegrationsConfig
              )}
              isMissedPunchRequestEnabled={shiftState.metadata.isMissedPunchRequestEnabled}
              isEnforceGeofenceTimestampsEnabled={isEnforceGeofenceTimestampsEnabled}
              verifiedClockOut={
                shift.verified && shift.clockInOut?.end ? parseISO(shift.clockInOut.end) : undefined
              }
              shiftId={shiftId}
              workerId={workerId}
              workplaceId={workplaceId}
              onConfirm={(newClockOutTime) => {
                setValue("clockOutTime", newClockOutTime, { shouldValidate: true });
              }}
            />

            <EditBreakTimeBottomSheet
              timesheetFlow={timesheetFlow}
              modalState={editBreakModalState}
              breakStartTime={formBreakStartTime}
              defaultBreakDuration={minutesToDuration(mandatoryBreakDurationInMinutes)}
              breakDuration={calculateBreakDuration(formBreakStartTime, formBreakEndTime)}
              initialBreakSkipped={!isDefined(formBreakStartTime)}
              workplaceTimezone={workplaceTimezone}
              shiftStartAt={parseISO(shift.start)}
              shiftEndAt={parseISO(shift.end)}
              clockInTime={formClockInTime}
              clockOutTime={formClockOutTime}
              verifiedBreakStartTime={
                shift.verified && shift.lunchInOut?.start
                  ? parseISO(shift.lunchInOut.start)
                  : undefined
              }
              verifiedBreakEndTime={
                shift.verified && shift.lunchInOut?.end ? parseISO(shift.lunchInOut.end) : undefined
              }
              isShiftVerified={shift.verified ?? false}
              isMissedPunchRequestEnabled={shiftState.metadata.isMissedPunchRequestEnabled}
              shiftId={shiftId}
              workerId={workerId}
              workplaceId={workplaceId}
              onConfirm={(newBreakStartTime, newBreakDuration) => {
                setValue("breakAuthorizationReason", undefined);
                applyBreakChange(newBreakStartTime, newBreakDuration);
              }}
            />
          </>
        )}

        {/* Timesheet Submitted Dialog (Pass Phone Back) */}
        <TimesheetSubmittedDialog
          modalState={timesheetSubmittedModalState}
          isCaliforniaFacility={isCaliforniaFacility}
          onContinue={async () => {
            timesheetSubmittedModalState.closeModal();

            if (isCaliforniaFacility) {
              setCurrentStep("worker-signing");
              // Scroll to top for worker signature section
              scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        />

        {/* Confirmation Bottom Sheet */}
        <SubmitUnverifiedTimesheetConfirmationBottomSheet
          modalState={submitUnverifiedTimesheetConfirmationModalState}
          onContinue={() => {
            history.replace(returnPath);
          }}
        />
      </PageWrapper>
    </ThemeProvider>
  );
}
/* eslint-enable max-lines */
